// Jotform helpers shared by /pays (legacy retainer popup) and the retainer ABN gate
// (RETAINER_ABN_SUPPLIER_PLAN.md 5.8). Moved out of pays.jsx unchanged, plus the
// hidden hostRef / flowToken pre-fill and a reader for Jotform's browser messages.

function isJotformHost(host) {
  const h = String(host || '').toLowerCase();
  return h === 'jotform.com' || h.endsWith('.jotform.com');
}

export function normalizeJotformEmbedUrl(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';

  try {
    const source = new URL(raw);
    if (!isJotformHost(source.hostname)) return '';

    const parts = source.pathname.split('/').filter(Boolean);
    const jsformIndex = parts.indexOf('jsform');
    const formIndex = parts.indexOf('form');
    const formId =
      (jsformIndex >= 0 && parts[jsformIndex + 1]) ||
      (formIndex >= 0 && parts[formIndex + 1]) ||
      parts.find((part) => /^\d{6,}$/.test(part)) ||
      '';

    if (!/^\d{6,}$/.test(formId)) return '';

    const embed = new URL(`https://form.jotform.com/jsform/${formId}`);
    source.searchParams.forEach((value, key) => embed.searchParams.set(key, value));
    return embed.toString();
  } catch {
    return '';
  }
}

export function jotformFormIdFromEmbedUrl(embedUrl) {
  const m = /\/jsform\/(\d{6,})/.exec(String(embedUrl || ''));
  return m ? m[1] : '';
}

// The retainer form link Edit config starts with: the stored link whenever one was saved (a
// stored blank stays blank, since blank disables the embed), else the built-in default (Club
// Astra before its first save). Codex R1-M2.
export function initialRetainerFormLink(storedRetainerForm, builtInLink) {
  const stored = storedRetainerForm && storedRetainerForm.jotformUrl;
  return typeof stored === 'string' ? stored : builtInLink || '';
}

// `hidden` = { hostRef, flowToken } for the ABN flow's forms; omitted for the legacy popup.
export function buildRetainerJotformScriptSrc(baseEmbedUrl, profile, hidden) {
  if (!baseEmbedUrl) return '';

  const url = new URL(baseEmbedUrl);
  const setParam = (key, value) => {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  };

  // Jotform Field - Firestore Field
  // hostFee        <- retainerAmount
  // fullName       <- name
  // dateOf         <- dateOfBirth
  // email          <- email
  // mobileNumber   <- mobile
  // instagramHandle<- instagram
  // invitedBy      <- manager
  // hostRef        <- "<eventId>/<users doc id>"   (ABN flow only, hidden)
  // flowToken      <- the flow's token             (ABN flow only, hidden)
  setParam('hostFee', profile?.retainerAmount);
  setParam('fullName', profile?.name);

  if (profile?.dateOfBirth) {
    let jsDate = null;
    if (typeof profile.dateOfBirth?.toDate === 'function') jsDate = profile.dateOfBirth.toDate();
    else jsDate = new Date(profile.dateOfBirth);

    if (jsDate && !Number.isNaN(jsDate.getTime())) {
      url.searchParams.set('dateOf[day]', String(jsDate.getDate()).padStart(2, '0'));
      url.searchParams.set('dateOf[month]', String(jsDate.getMonth() + 1).padStart(2, '0'));
      url.searchParams.set('dateOf[year]', String(jsDate.getFullYear()));
    }
  }

  setParam('email', profile?.email);
  setParam('mobileNumber', profile?.mobile);
  setParam('instagramHandle', profile?.instagram);
  setParam('invitedBy', profile?.manager);
  if (hidden) {
    setParam('hostRef', hidden.hostRef);
    setParam('flowToken', hidden.flowToken);
  }

  return url.toString();
}

// Jotform's "submission-completed" browser message (spike: carries formID and
// submissionID, never answers). Returns { formID, submissionID } or null. Only trusts
// messages from a jotform.com origin (strict hostname, not a substring match).
export function readJotformSubmitMessage(event) {
  let host = '';
  try {
    host = new URL(event?.origin).hostname;
  } catch {
    return null;
  }
  if (!isJotformHost(host)) return null;
  let data = event?.data;
  if (typeof data === 'string') {
    if (data === 'submission-completed') return { formID: '', submissionID: '' };
    if (data.charAt(0) !== '{') return null;
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object' || data.action !== 'submission-completed') return null;
  const formID = /^\d{6,25}$/.test(String(data.formID || '')) ? String(data.formID) : '';
  const submissionID = /^\d{1,25}$/.test(String(data.submissionID || '')) ? String(data.submissionID) : '';
  return { formID, submissionID };
}
